/**
 * Process-wide cache of decoded `AudioBuffer`s keyed by URL, coalescing
 * the in-flight byte download that feeds each decode, with a tiny
 * observable so the loading UI can show live download progress.
 *
 * Decoding a multi-MB MP3 takes 50-200ms on a low-end laptop, which is
 * exactly the moment the player taps Start - blocking it produces a
 * terrible "Start did nothing" perception. We decode once per URL (and
 * coalesce the preload warm-up with the post-Start download when they
 * overlap) and hand back the same decoded buffer on every retry / replay
 * attempt.
 *
 * Buffers are immutable from Web Audio's perspective so sharing them
 * across `AudioBufferSourceNode`s in different AudioContexts works as
 * long as both contexts share the same sample rate (Chrome reuses the
 * device default; we don't pin one). On the rare mismatch the player
 * just re-decodes for that context - acceptable cost for the simplicity.
 */

/** Live byte-download progress for one audio URL, surfaced to the loading UI. */
export interface AudioLoadProgress {
  /** Bytes received so far. */
  receivedBytes: number;
  /**
   * Total bytes expected from the `Content-Length` header, or null when
   * the server didn't send one (progress is then indeterminate).
   */
  totalBytes: number | null;
  /** True once the full file has been received. */
  done: boolean;
}

interface DecodeEntry {
  /**
   * Pending decode. Resolves once the buffer is ready. We store the
   * *promise* (not the buffer) so concurrent requests for the same URL
   * coalesce onto a single decode.
   */
  promise: Promise<AudioBuffer>;
  /**
   * The AudioContext the buffer was decoded against. If a later request
   * comes from a different context we re-decode (Web Audio doesn't share
   * decoded buffers across contexts when sample rates differ).
   */
  contextSampleRate: number;
}

const decodeCache = new Map<string, DecodeEntry>();
/** In-flight raw-byte downloads keyed by URL; coalesces preload + decode. */
const bytesCache = new Map<string, Promise<ArrayBuffer>>();
const progressStore = new Map<string, AudioLoadProgress>();
const progressListeners = new Map<string, Set<() => void>>();

function setProgress(url: string, progress: AudioLoadProgress): void {
  progressStore.set(url, progress);
  const listeners = progressListeners.get(url);
  if (!listeners) {
    return;
  }
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Subscribe to download-progress changes for `url`; returns an
 * unsubscribe function. Shaped for React's `useSyncExternalStore` but
 * framework-agnostic so this module stays UI-free.
 */
export function subscribeAudioLoadProgress(url: string, listener: () => void): () => void {
  let listeners = progressListeners.get(url);
  if (!listeners) {
    listeners = new Set();
    progressListeners.set(url, listeners);
  }
  listeners.add(listener);
  return () => {
    const set = progressListeners.get(url);
    if (!set) {
      return;
    }
    set.delete(listener);
    if (set.size === 0) {
      progressListeners.delete(url);
    }
  };
}

/**
 * Latest progress snapshot for `url`, or null if no download has begun.
 * Returns a stable reference between updates so `useSyncExternalStore`
 * doesn't loop.
 */
export function getAudioLoadProgressSnapshot(url: string): AudioLoadProgress | null {
  return progressStore.get(url) ?? null;
}

/**
 * Download `url`'s bytes, streaming the response so we can report
 * received/total progress to {@link subscribeAudioLoadProgress}
 * listeners. Falls back to a single buffered read when the response
 * body isn't a readable stream (very old browsers / some test shims),
 * in which case progress jumps straight from 0 to done.
 */
async function downloadAudioBytes(url: string): Promise<ArrayBuffer> {
  setProgress(url, { receivedBytes: 0, totalBytes: null, done: false });
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch audio (${res.status}): ${url}`);
  }
  const lengthHeader = res.headers.get('Content-Length');
  const declaredTotal = lengthHeader ? Number(lengthHeader) : NaN;
  const totalBytes = Number.isFinite(declaredTotal) && declaredTotal > 0 ? declaredTotal : null;

  if (!res.body || typeof res.body.getReader !== 'function') {
    const buffer = await res.arrayBuffer();
    const size = buffer.byteLength;
    setProgress(url, { receivedBytes: size, totalBytes: totalBytes ?? size, done: true });
    return buffer;
  }

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;
  setProgress(url, { receivedBytes: 0, totalBytes, done: false });
  for (;;) {
    const result = await reader.read();
    if (result.done) {
      break;
    }
    const chunk = result.value;
    chunks.push(chunk);
    received += chunk.byteLength;
    setProgress(url, { receivedBytes: received, totalBytes, done: false });
  }

  const merged = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.byteLength;
  }
  // A gzip/transfer-encoding proxy can make the decoded stream larger
  // than the advertised Content-Length, so settle the total to whichever
  // is bigger — the bar must never read past 100% or "X / <X KB".
  setProgress(url, {
    receivedBytes: received,
    totalBytes: Math.max(received, totalBytes ?? 0),
    done: true,
  });
  return merged.buffer;
}

/**
 * Coalesces concurrent byte downloads for `url`: a preload warm-up and
 * the post-Start decode that lands while it's still in flight share the
 * single request (and its progress stream) instead of racing two
 * downloads. The entry is dropped once settled so the raw bytes aren't
 * pinned in memory — a later request (replay, or Start after the preload
 * already finished) just re-reads from the browser's HTTP cache.
 */
function loadAudioBytes(url: string): Promise<ArrayBuffer> {
  const existing = bytesCache.get(url);
  if (existing) {
    return existing;
  }
  const promise = downloadAudioBytes(url);
  bytesCache.set(url, promise);
  const clear = (): void => {
    // Guard against clobbering a newer in-flight download for the same URL.
    if (bytesCache.get(url) === promise) {
      bytesCache.delete(url);
    }
  };
  promise.then(clear, clear);
  return promise;
}

export async function fetchAndDecodeAudio(
  ctx: AudioContext,
  url: string,
): Promise<AudioBuffer> {
  const existing = decodeCache.get(url);
  if (existing && existing.contextSampleRate === ctx.sampleRate) {
    return existing.promise;
  }
  const promise = (async () => {
    const bytes = await loadAudioBytes(url);
    // decodeAudioData detaches its input buffer; hand it a copy so a
    // coalesced awaiter of the same download (the preload, or a decode for
    // another context) isn't left holding a detached buffer.
    return ctx.decodeAudioData(bytes.slice(0));
  })();
  decodeCache.set(url, { promise, contextSampleRate: ctx.sampleRate });
  // Pre-emptively evict on failure so the next attempt retries the full
  // download+decode rather than re-using a rejected promise. Guard against
  // clobbering a newer entry (a decode for a different sample rate) that
  // replaced this one before this promise rejected.
  promise.catch(() => {
    if (decodeCache.get(url)?.promise === promise) {
      decodeCache.delete(url);
    }
  });
  return promise;
}

/** Test helper - drop all caches so tests don't leak buffers across runs. */
export function clearAudioCacheForTest(): void {
  decodeCache.clear();
  bytesCache.clear();
  progressStore.clear();
  progressListeners.clear();
}

/**
 * Warm the track by downloading its bytes ahead of time. Called from the
 * play pages as soon as the chart is known, so by the time the player taps
 * Start the audio is already downloaded — warm in the browser's HTTP cache,
 * or still in flight (in which case the post-Start decode coalesces onto
 * this same request via {@link loadAudioBytes}). Either way the
 * (post-gesture) AudioContext decodes it with little or no network wait,
 * shrinking the cold-start gap that otherwise shows as a black canvas on
 * slow devices. Sharing the download path also means it feeds the loading
 * overlay's progress bar.
 *
 * Decoding can't be pre-warmed here: it needs an AudioContext, which iOS
 * only unlocks on the Start gesture. Failures are logged but never
 * surfaced — preloading is purely an optimisation, and the real
 * fetch+decode in {@link fetchAndDecodeAudio} still runs (and reports
 * its own errors) when the player starts.
 */
export function preloadAudioFile(url: string): void {
  if (typeof fetch !== 'function') {
    return;
  }
  loadAudioBytes(url).catch((err) => {
    console.warn('Failed to preload audio file:', url, err);
  });
}
